import { Box, Button, Container, Stack, Typography, useMediaQuery, Theme } from '@mui/material';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import PhoneIcon from '@untitled-ui/icons-react/build/esm/Phone';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';
import { useCompanySettings } from 'src/hooks/use-company-settings';

const CALCOM_URL = 'https://cal.com/relight/in-person-quote';

const Page: PageType = () => {
  usePageView();
  const { settings: co } = useCompanySettings();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <>
      <Seo
        title="Schedule a Quote | Mystic Media Film"
        description="Book a free strategy call with Mystic Media Film. Our Houston video production and marketing team will scope your project and quote it in days, not weeks."
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: '#000000',
          minHeight: '100vh',
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            py: 1.5,
            px: { xs: 1, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                component={RouterLink}
                href={paths.index}
                startIcon={<ArrowLeftIcon />}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Back
              </Button>
              <Button
                component={RouterLink}
                href="tel:+18138150142"
                startIcon={<PhoneIcon />}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: 'transparent',
                  },
                }}
              >
                (813) 815-0142
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 5 }, px: { xs: 1.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 2, lg: 6 }}
            alignItems={{ xs: 'center', lg: 'flex-start' }}
          >
            {/* Left Side - Info */}
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', lg: 400 },
                textAlign: { xs: 'center', lg: 'left' },
                pt: { xs: 0, lg: 4 },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.75rem' },
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1.2,
                  mb: { xs: 1, md: 2 },
                }}
              >
                Book Your Free Quote
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', md: '1.125rem' },
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.6,
                  mb: { xs: 0, md: 4 },
                }}
              >
                Schedule a quick visit from one of our lighting specialists.
                {mdUp && " We'll measure your property and provide an exact quote on the spot."}
              </Typography>

              {/* Benefits - Desktop only */}
              {mdUp && (
                <Stack spacing={2.5}>
                  {[
                    { title: 'Free & No Obligation', desc: 'Zero cost, no pressure to commit' },
                    { title: '15-30 Minutes', desc: 'Quick visit at your convenience' },
                    { title: 'Exact Pricing', desc: 'Know your cost before you decide' },
                  ].map((item) => (
                    <Stack key={item.title} direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#2970FF',
                          mt: 0.8,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: '#fff',
                            mb: 0.25,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: 'rgba(255,255,255,0.5)',
                          }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Contact info - Desktop only */}
              {mdUp && (
                <Box sx={{ mt: 5, pt: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      mb: 1.5,
                    }}
                  >
                    Questions?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    Call us at{' '}
                    <Box
                      component="a"
                      href="tel:+18138150142"
                      sx={{
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      (813) 815-0142
                    </Box>
                    {' '}or email{' '}
                    <Box
                      component="a"
                      href="mailto:sales@mysticmediafilm.com"
                      sx={{
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      sales@mysticmediafilm.com
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'rgba(255,255,255,0.7)',
                      mt: 2,
                    }}
                  >
                    To schedule a free quote with a live
                    <br />
                    agent call{' '}
                    <Box
                      component="a"
                      href="tel:+14252222021"
                      sx={{
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      (425) 222-2021
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right Side - Calendar */}
            <Box
              sx={{
                flex: 1,
                width: '100%',
                maxWidth: { xs: '100%', lg: 800 },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: { xs: 'transparent', md: 'rgba(255,255,255,0.02)' },
                  borderRadius: { xs: 0, md: 3 },
                  border: 'none',
                  overflow: 'hidden',
                  // Fixed container height — iframe extends beyond this so the Cal.com
                  // watermark at the bottom is clipped off by overflow:hidden
                  height: { xs: 480, sm: 540, md: 640 },
                  mx: { xs: -1.5, sm: 0 },
                }}
              >
                {/* Top fade overlay to soften the iframe top edge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: { xs: 40, sm: 80, md: 200 },
                    background: {
                      xs: 'linear-gradient(to bottom, #000000 0%, transparent 100%)',
                      md: 'linear-gradient(to bottom, #000000 0%, #000000 30%, transparent 100%)',
                    },
                    zIndex: 1,
                    pointerEvents: 'none',
                    borderRadius: { xs: 0, md: '12px 12px 0 0' },
                  }}
                />
                <Box
                  component="iframe"
                  src={`${CALCOM_URL}?embed=true&theme=dark&hideBranding=true`}
                  title="Schedule In-Person Quote"
                  allow="camera; microphone; autoplay; encrypted-media"
                  sx={{
                    border: 'none',
                    width: '100%',
                    // Taller than container to push watermark out of view
                    height: { xs: 680, sm: 750, md: 850 },
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          </Stack>

          {/* Mobile Footer */}
          {!mdUp && (
            <Box sx={{ mt: 3, textAlign: 'center', pb: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                {[
                  { label: 'Free', icon: '✓' },
                  { label: '15-30 min', icon: '✓' },
                  { label: 'Exact pricing', icon: '✓' },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      fontSize: '0.7rem',
                      color: 'rgba(255,255,255,0.5)',
                      bgcolor: 'rgba(255,255,255,0.05)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '100px',
                      fontWeight: 500,
                    }}
                  >
                    {item.label}
                  </Box>
                ))}
              </Stack>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.4)',
                }}
              >
                Questions? Call{' '}
                <Box
                  component="a"
                  href="tel:+18138150142"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  (813) 815-0142
                </Box>
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Page;
